import { Link } from "@tanstack/react-router";
import { ArrowUpIcon, InstagramIcon } from "./icons";
import { Logo } from "./Logo";
import { Leaf } from "./Leaf";
import { WaveDivider } from "./WaveDivider";
import { useCookieConsent } from "@/hooks/useCookieConsent";


type NavItem = { label: string; href?: string; to?: string };

const NAV: NavItem[] = [
  { to: "/hatha-yoga", label: "Hatha Yoga" },
  { href: "#ueber-mich", label: "Über mich" },
  { href: "#angebot", label: "Yoga Angebot" },
  { href: "#termine", label: "Termine" },
  { href: "#kontakt", label: "Kontakt" },
];

const LEGAL = [
  { to: "/impressum", label: "Impressum" },
  { to: "/datenschutz", label: "Datenschutz" },
  { to: "/agb", label: "AGB" },
  { to: "/barrierefreiheit", label: "Barrierefreiheit" },
];

function CookieSettingsLink() {
  const { resetConsent } = useCookieConsent();
  return (
    <button
      type="button"
      onClick={resetConsent}
      className="inline-flex min-h-11 items-center transition-colors hover:text-perl"
    >
      Cookie-Einstellungen
    </button>
  );
}


export function Footer() {
  return (
    <>
      <WaveDivider from="bg-cream-sun" to="var(--color-emerald-deep)" />
      <footer
        aria-label="Fußbereich"
        className="relative overflow-hidden bg-emerald-deep text-perl"
      >
        <div className="relative mx-auto max-w-6xl px-5 py-14 md:px-8">
          {/* Top row: logo + nav + social */}
          <div className="flex flex-col items-center gap-10 md:flex-row md:items-center md:justify-between">
            <Logo variant="light" tagline="Yoga · Balance · Verbindung" />

            <nav aria-label="Footer-Navigation">
              <ul className="flex flex-wrap items-center justify-center gap-6 md:gap-8">
                {NAV.map((item) => (
                  <li key={item.label}>
                    <Link
                      to={item.to ?? "/"}
                      hash={item.to ? undefined : item.href?.replace("#", "")}
                        className="relative inline-flex min-h-11 items-center text-sm font-medium text-perl/85 transition-colors hover:text-[#DBB066] after:absolute after:bottom-2 after:left-0 after:h-[1px] after:w-full after:origin-left after:scale-x-0 after:bg-[#DBB066]/80 after:transition-transform after:duration-300 hover:after:scale-x-100"
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>

            <a
              href="https://www.instagram.com/flowwithmaharani/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram Profil von flowwithmaharani"
              className="inline-flex size-12 items-center justify-center rounded-full border border-perl/40 text-perl transition hover:border-gold hover:text-gold"
            >
              <InstagramIcon className="size-5" />
            </a>

            <button
              type="button"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              aria-label="Nach oben scrollen"
              className="inline-flex size-12 items-center justify-center rounded-full border border-perl/40 text-perl transition hover:border-gold hover:text-gold"
            >
              <ArrowUpIcon className="size-5" />
            </button>
          </div>

          {/* Divider */}
          <div className="my-10 border-t border-perl/20" />

          {/* Bottom row: copyright + legal */}
          <div className="flex flex-col items-center gap-4 text-sm text-perl/70 md:flex-row md:justify-between">
            <p>© {new Date().getFullYear()} Jessica Maharani Singh · lotus-flow.de</p>
            <div className="flex flex-wrap items-center justify-center gap-1">
              {LEGAL.map((item, i) => (
                <span key={item.to} className="flex items-center">
                  <Link
                    to={item.to}
                    className="inline-flex min-h-11 items-center transition-colors hover:text-perl"
                  >
                    {item.label}
                  </Link>
                  {i < LEGAL.length - 1 && (
                    <span className="mx-1.5" aria-hidden>·</span>
                  )}
                </span>
              ))}
              <span className="mx-1.5" aria-hidden>·</span>
              <CookieSettingsLink />
            </div>
          </div>
        </div>

        {/* Botanical accent */}
        <Leaf
          className="absolute -bottom-6 -right-4 text-perl/10 md:right-4"
          width={160}
          rotate={12}
        />
      </footer>
    </>
  );
}
